(ns hai.home.desktop.mini-apps.progress
  "My Progress mini-app card: today's meetings and completed-task stamps laid
   out on a single horizontal time axis. Calendar state, layout, and meeting
   accessors come from [[hai.home.calendar]]; this card owns the progress
   timeline rendering, and [[hai.home.day-pills]] the row of deadlines and
   all-day entries above it. The header title links to the full progress
   timeline."
  (:require
   [hai.datetime :as datetime]
   [hai.home.calendar :as calendar]
   [hai.home.clock :as clock]
   [hai.home.day-pills :as day-pills]
   [hai.mini-apps.data :as mini-apps-data]
   [hai.mini-apps.frame :as mini-apps]
   [hai.home.progress :as progress]
   [hai.tasks.data :as tasks-data]
   [hai.tasks.ui :as ui]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; Same glyph as the navbar's "Progress timeline" route (see hai.journal2.routes).
(def ^:private timeline-icon (icons/mui-icon :outlined :timeline))

;; ---------------------------------------------------------------------------
;; Block placement
;; ---------------------------------------------------------------------------

;; Lane 0 = base green; subsequent lanes only used when events overlap.
(def ^:private event-palette-cycle
  (cycle ["#A8BA93"   ;; base green
          "#F4B7AE"   ;; coral
          "#F7D7AB"   ;; peach
          "#A8C9D6"   ;; sky
          "#C5B6D8"   ;; lilac
          "#E8B7D0"])) ;; pink

(defn- lane-color [lane]
  (nth event-palette-cycle (or lane 0)))

(defn- desktop-block-style
  "Inline style for a horizontal-strip .block. Time → main axis (left/width);
   cross-axis layout record → vertical band (top/height)."
  [layout left-pct width-pct]
  (let [{:keys [lane overlapping? cross-start cross-size]} layout]
    (cond-> {:background (lane-color lane)
             :left       (str left-pct "%")
             :width      (str width-pct "%")
             :top        (str (* 100 cross-start) "%")
             :height     (str (* 100 cross-size) "%")}
      overlapping? (assoc :opacity "0.7"))))

;; ---------------------------------------------------------------------------
;; Completed-task stamps
;; ---------------------------------------------------------------------------

(defn- stamp-tooltip-content [group-tasks]
  (r/as-element
   [:div
    (for [task group-tasks]
      ^{:key (:task/uuid task)}
      [:div {:style {:display "flex" :gap "8px" :align-items "baseline"}}
       [:span {:style {:font-weight 600}} (:task/title task)]
       [:span {:style {:opacity 0.65 :font-size "12px"}}
        (datetime/format-time (:task/completed-at task))]])]))

;; ---------------------------------------------------------------------------
;; Card frame (soft sky-blue header from the shared card palette)
;; ---------------------------------------------------------------------------

(o/defstyled card mini-apps/card
  {:margin-bottom "28px"}
  ;; Duck-tape strip on the right, mirroring the notes/tasks cards it sits above.
  [:&::before {:left "auto" :right "40px"}]
  [:.app-header
   {:background t/--progress
    :border-bottom "1px solid" :border-bottom-color t/--card-forest-10}]
  ;; :timeline is a Material fill icon, unlike the stroke icons the shared card
  ;; frame assumes, so restore fill and match the other header icons' size.
  [:.app-icon [:svg {:fill "currentColor" :width "20px" :height "20px"}]]
  ;; Body padding lives here since the shared card frame leaves the body to each
  ;; mini-app (the header band supplies its own padding).
  [:.progress-body
   {:padding "18px 22px 22px"}]
  [:.progress-subtitle
   {:font-size "12px" :font-weight "500"
    :color t/--property-15
    :margin "0 0 4px"
    :display "flex" :flex-wrap "wrap" :align-items "center"
    :column-gap "6px" :row-gap "4px"}
   [:strong {:color t/--card-forest :font-weight "700"}]]
  [:.timeline-row
   {:position "relative" :height "80px" :margin-bottom "30px"
    :border-radius "10px"
    :background "repeating-linear-gradient(-45deg, rgba(184,196,166,0.08), rgba(184,196,166,0.08) 4px, rgba(184,196,166,0.02) 4px, rgba(184,196,166,0.02) 8px)"}]
  [:.block
   ;; top + height come from inline style (cross-axis lane); see desktop-block-style.
   {:border-radius "10px" :position "absolute"
    :cursor "pointer"
    :box-shadow "0 1px 3px rgba(45,62,42,0.18)"
    :transition "transform 0.15s, box-shadow 0.15s, opacity 0.15s"
    :z-index "1"
    ;; Title sits inside the strip, vertically centered; ellipsis on width.
    :display "flex" :align-items "center"
    :padding "0 8px" :overflow "hidden"}
   [:.block-title
    {:font-size "11px" :font-weight "700"
     :color t/--card-forest
     :line-height "1.2"
     :white-space "nowrap" :overflow "hidden" :text-overflow "ellipsis"
     :width "100%"
     :pointer-events "none" :user-select "none"}]
   [:&:hover
    {:transform "translateY(-2px)"
     :opacity "1 !important"
     :box-shadow "0 6px 16px rgba(45,62,42,0.28)"
     :z-index "5"}]]
  [:.block.highlighted
   {:transform "translateY(-3px)"
    :opacity "1 !important"
    :box-shadow "0 6px 16px rgba(45,62,42,0.28)"
    :z-index "5"}]
  ;; Events overlapping "now" are highlighted by the ring only. The single
  ;; "now" time marker lives on the axis (.now-line), not per-event.
  [:.block.now
   {:box-shadow "0 0 0 3px rgba(196,69,69,0.35), 0 1px 3px rgba(45,62,42,0.18)"}]
  ;; 44px of headroom above the lane so the stamp layer never clips.
  [:.timeline-wrap {:position "relative" :padding-top "44px"}]
  ;; Completion stamps float above the lane, on top of the now pin (task
  ;; wins). Bursts overlap and fan out on hover.
  [:.stamp-row
   {:position "absolute"
    :bottom "100%"
    :margin-bottom "8px"
    :transform "translateX(-50%)"
    :display "flex"
    :z-index "6"
    :cursor "default"}
   [:.progress-stamp
    {:width "26px" :height "26px"
     :flex "0 0 auto"
     :transition "margin 160ms ease"}
    ;; Opaque here, unlike the translucent stampbox impression: stamps in a
    ;; burst occlude the ones behind, so the front of the stack stays crisp.
    [ui/completion-stamp
     {:width "100%" :height "100%"
      :background-color t/--card-surface
      :border-color t/--card-forest
      :color t/--card-forest
      :mix-blend-mode "normal"
      :opacity "1"}]
    [:.check {:font-size "18px"}]]
   [:.progress-stamp+.progress-stamp {:margin-left "-18px"}]
   [:&:hover
    [:.progress-stamp+.progress-stamp {:margin-left "5px"}]]]
  ;; Single "now" marker on the time axis: vertical line through the band with
  ;; a time pill on top (mirrors the mobile strip's now-line + label).
  [:.now-line
   ;; Extends 6px below the band to meet the time pill (see .now-label margin).
   {:position "absolute" :top "0" :bottom "-6px" :width "0"
    :border-left "2px solid #C44545"
    :transform "translateX(-50%)"
    :z-index "5" :pointer-events "none"}
   [:.now-label
    {:position "absolute" :left "50%" :top "100%"
     :transform "translateX(-50%)"
     :background "#C44545" :color "white"
     :font-size "10px" :font-weight "700"
     :padding "2px 7px" :border-radius "999px"
     :white-space "nowrap"
     :font-variant-numeric "tabular-nums"
     :box-shadow "0 1px 2px rgba(45,62,42,0.20)"}]]
  [:.axis
   {:display "flex" :justify-content "space-between"
    :padding "8px 4px 0" :font-size "11px" :color "#6B7560"}])

(defn- meeting-block
  "One meeting bar on the band. Subscribes to its own hover flag so a hover
   change re-renders just the two affected blocks, not the whole panel."
  [{:keys [meeting layout left-pct width-pct]}]
  (let [status   (::calendar/status meeting)
        title    (calendar/m-title meeting)
        uuid     (:journal.entry/uuid meeting)
        hovered? @(<s [::calendar/meeting-hovered? uuid])]
    [c/Tooltip {:content (or title "Meeting")}
     [:div.block
      {:class (str (name (or status :upcoming))
                   (when hovered? " highlighted"))
       :style (desktop-block-style layout left-pct width-pct)
       :on-mouse-enter #(>e [::calendar/hover-meeting uuid])
       :on-mouse-leave #(>e [::calendar/hover-meeting nil])}
      (when title [:span.block-title title])]]))

(defn panel [{title :mini-app/title}]
  (let [meetings @(<s [::calendar/timed-meetings])
        all-day  @(<s [::calendar/all-day-meetings])
        deadlines @(<s [::tasks-data/deadlines-today])
        completions @(<s [::tasks-data/completed-today])
        win (progress/compute-window meetings completions)
        axis-hours (progress/axis-hours win)
        layout     @(<s [::calendar/meeting-layout])
        ;; Position each meeting absolutely by start/end percent of window.
        ;; Allows overlapping events to visually stack (shadow + hover-z handle layering).
        positioned (->> meetings
                        (keep (fn [m]
                                (when-let [{:keys [offset-pct extent-pct]}
                                           (progress/meeting-span-pct m win)]
                                  {:meeting m
                                   :layout (get layout (:journal.entry/uuid m))
                                   :left-pct  offset-pct
                                   :width-pct extent-pct}))))
        now-ms @(<s [::clock/now-ms])
        now-pct (progress/now-pct now-ms win)
        stamp-groups (progress/cluster-completions completions)
        done-count (count completions)
        meeting-count (count meetings)
        collapsed? @(<s [::mini-apps-data/collapsed? :progress])]
    [card {:class (when collapsed? "collapsed")}
     [:div.app-header
      [:a.app-title-link
       {:href (routing/href :timelines/view) :target routing/link-target}
       [:span.app-icon [timeline-icon]]
       [:h2.app-title title]]
      [:div.app-actions
       [mini-apps/collapse-toggle
        {:collapsed? collapsed?
         :label      "progress"
         :class      "app-action-btn"
         :on-toggle  #(>e [::mini-apps-data/toggle-collapsed :progress])}]]]
     (when-not collapsed?
       [:div.progress-body
        (when (or (pos? done-count) (seq all-day) (seq deadlines))
          [:div.progress-subtitle
           ;; The counts live in one span so the flex row (needed for the pills)
           ;; doesn't swallow the spaces between the sentence's text nodes.
           (when (pos? done-count)
             [:span
              (when (pos? meeting-count)
                [:<>
                 [:strong (str meeting-count)]
                 (if (= 1 meeting-count) " meeting · " " meetings · ")])
              [:strong (str done-count)]
              (if (= 1 done-count) " task done" " tasks done")
              " — steady pace"])
           (when (or (seq deadlines) (seq all-day))
             [day-pills/day-pills {:deadlines deadlines :all-day all-day}])])
        [:div.timeline-wrap
         [:div.timeline-row
          (doall
           (for [{:keys [meeting] :as block} positioned]
             ^{:key (:journal.entry/uuid meeting)}
             [meeting-block block]))
          (doall
           (for [{:keys [tasks mean-ms]} stamp-groups]
             (let [pct (progress/stamp-pct mean-ms win)]
               ^{:key (:task/uuid (first tasks))}
               [c/Tooltip {:content (stamp-tooltip-content tasks)}
                [:div.stamp-row {:style {:left (str pct "%")}}
                 (doall
                  (for [task tasks]
                    ^{:key (:task/uuid task)}
                    [:div.progress-stamp
                     [ui/completion-stamp {:task-title (:task/title task)}]]))]])))
          (when now-pct
            [:div.now-line {:style {:left (str now-pct "%")}}
             [:span.now-label (datetime/format-time (js/Date. now-ms))]])]]
        [:div.axis
         (doall
          (for [h axis-hours]
            (datetime/format-hour-recognizable h)))]])]))
